import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useRegistrationRouteState } from '../features/registration/model';
import {
  OnboardingCtaButton,
  OnboardingSlideContent,
} from '../features/registration/ui';

const FOOTER_GAP_PX = 14;
const FOOTER_BOTTOM_PADDING_PX = 8;
const FOOTER_SECONDARY_MIN_HEIGHT_PX = 36;

const Screen = styled.div.attrs({
  'data-ui': 'onboarding-page',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const SlideTrack = styled.div.attrs({
  'data-ui': 'onboarding-content',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  & > [data-ui^='onboarding-'][data-ui$='-slide'] {
    margin-bottom: auto;
  }
`;

const FooterNoteSlot = styled.div.attrs({
  'data-ui': 'onboarding-footer-note-slot',
})`
  width: ${({ theme }) => theme.scale.px(269)};
`;

const DisclaimerText = styled.p.attrs({
  'data-ui': 'onboarding-disclaimer',
})`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(10)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.scale.px(12)};
  color: rgba(0, 0, 0, 0.8);
`;

const Footer = styled.div.attrs({
  'data-ui': 'onboarding-page-footer',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(FOOTER_GAP_PX)};
  padding-bottom: ${({ theme }) => theme.scale.px(FOOTER_BOTTOM_PADDING_PX)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'onboarding-page-footer-secondary',
})`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(FOOTER_SECONDARY_MIN_HEIGHT_PX)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

function resolveOnboardingCtaLabelKey(slideKey: string): string {
  switch (slideKey) {
    case 'costs':
      return 'registrationProfileCosts.cta';
    case 'benefits':
      return 'registrationProfileBenefits.cta';
    default:
      return 'registrationProfile.cta';
  }
}

export function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const registrationRoute = useRegistrationRouteState();

  const handleContinue = () => {
    const nextPath = registrationRoute.nextPath;
    if (!nextPath) {
      navigate('/login');
      return;
    }

    navigate(nextPath);
  };

  return (
    <Screen>
      <SlideTrack>
        <OnboardingSlideContent slideKey={registrationRoute.onboardingSlide} />
      </SlideTrack>
      <Footer>
        <OnboardingCtaButton
          label={t(
            resolveOnboardingCtaLabelKey(registrationRoute.onboardingSlide),
          )}
          onPress={handleContinue}
        />
        <FooterSecondary>
          {registrationRoute.onboardingSlide === 'welcome' ? (
            <FooterNoteSlot>
              <DisclaimerText>{t('registrationProfile.notice')}</DisclaimerText>
            </FooterNoteSlot>
          ) : null}
        </FooterSecondary>
      </Footer>
    </Screen>
  );
}
